import React, {useState} from 'react';
import {Alert, Box, Button, TextField, Typography} from '@mui/material';
import {useMutation} from '@apollo/client';
import {useSnackbar} from 'notistack';
import {ScrollableModal} from '../../components/Common/ScrollableModal';
import {Panel} from '../../components/Panel/Panel';
import {
  UpdateDefaultBankAccount,
  UpdateDefaultBankAccountVariables,
} from '../../schema-types/UpdateDefaultBankAccount';
import {getErrorMessage} from '../../utils/getErrorMessage';
import validateUUID from '../../utils/validateUUID';
import {UPDATE_DEFAULT_BANK_ACCOUNT} from './defaultBankAccountQueries';

export type SetDefaultBankAccountHolder = {id: string; simplifiedName: string | null};

type Props = {
  open: boolean;
  onClose: () => void;
  onSaved: () => void;
  accountId: string;
  bankAccount: {id: string; accountName: string | null};
  // The holders without a default whom this bank account can serve; never empty.
  holders: [SetDefaultBankAccountHolder, ...SetDefaultBankAccountHolder[]];
};

export const holderName = (holder: SetDefaultBankAccountHolder) =>
  holder.simplifiedName || 'client';

export const SetDefaultBankAccountModal: React.FC<Props> = ({
  open,
  onClose,
  onSaved,
  accountId,
  bankAccount,
  holders,
}) => {
  const [contactId, setContactId] = useState(holders[0].id);
  const [errors, setErrors] = useState<string[]>([]);
  const {enqueueSnackbar} = useSnackbar();
  const [updateDefaultBankAccount, {loading}] = useMutation<
    UpdateDefaultBankAccount,
    UpdateDefaultBankAccountVariables
  >(UPDATE_DEFAULT_BANK_ACCOUNT);

  const accountLabel = bankAccount.accountName || 'this bank account';
  const isChoice = holders.length > 1;
  const question = isChoice
    ? `Make ${accountLabel} the default bank account for the selected client?`
    : `Make ${accountLabel} the default bank account for ${holderName(holders[0])}?`;

  const submit = async () => {
    setErrors([]);
    try {
      const {data} = await updateDefaultBankAccount({
        variables: {
          accountId: validateUUID(accountId)!,
          contactId: validateUUID(contactId)!,
          bankAccountId: validateUUID(bankAccount.id)!,
        },
      });
      const result = data?.updateDefaultBankAccount;
      if (!result?.ok) {
        setErrors(
          result?.errors?.length
            ? result.errors
            : ['Could not save the default bank account.']
        );
        return;
      }
      enqueueSnackbar('Default bank account saved to Curo.', {variant: 'success'});
      onSaved();
      onClose();
    } catch (error) {
      setErrors([getErrorMessage(error, 'Could not reach the server.')]);
    }
  };

  return (
    <ScrollableModal size="small" autoHeight open={open} onClose={onClose}>
      <Panel title="Set default bank account">
        {isChoice ? (
          <TextField
            id="default-bank-account-holder"
            select
            SelectProps={{native: true}}
            label="Client"
            fullWidth
            size="small"
            InputLabelProps={{shrink: true}}
            value={contactId}
            onChange={(event) => setContactId(event.target.value)}
            sx={{mb: 2}}
          >
            {holders.map((holder) => (
              <option key={holder.id} value={holder.id}>
                {holderName(holder)}
              </option>
            ))}
          </TextField>
        ) : null}
        <Typography variant="body1">{question}</Typography>
        <Typography variant="body2" color="text.secondary" sx={{mt: 1}}>
          Curo uses this account when a new plan is set up for the client. Once saved,
          only platform support can change it.
        </Typography>
        {errors.length > 0 ? (
          <Alert severity="error" sx={{mt: 2}}>
            {errors.join(' ')}
          </Alert>
        ) : null}
        <Box mt={3} display="flex" justifyContent="flex-end" gap={1}>
          <Button variant="text" onClick={onClose} disabled={loading}>
            Cancel
          </Button>
          <Button variant="contained" color="primary" disabled={loading} onClick={submit}>
            Set as default
          </Button>
        </Box>
      </Panel>
    </ScrollableModal>
  );
};
